type Corner = { lat: number; lng: number };

/** Panels whose corners come within this distance belong to neighbouring strings. */
const NEIGHBOUR_DISTANCE_M = 1;

/**
 * A palette index per string, so no two strings with neighbouring panels share
 * a colour. Strings are coloured most-constrained first (DSatur), each taking
 * the lowest free index so the palette's earliest colours do most of the work.
 */
export const solarStringColourKeys = (
  strings: readonly { id: string; panelIds: readonly string[] }[],
  panels: readonly { id?: string; corners?: readonly Corner[] }[],
  paletteSize: number
): Map<string, number> => {
  const stringOfPanel = new Map<string, number>();
  strings.forEach((string, index) => {
    for (const id of string.panelIds) stringOfPanel.set(id, index);
  });
  const placed = panels.flatMap((panel) => {
    const string = panel.id == null ? undefined : stringOfPanel.get(panel.id);
    return string == null || !panel.corners?.length
      ? []
      : [{ string, corners: panel.corners }];
  });
  const origin = placed[0]?.corners[0];
  const metresPerLng = 111_320 * Math.cos(((origin?.lat ?? 0) * Math.PI) / 180);
  const projected = placed.map(({ string, corners }) => {
    const points = corners.map((corner) => ({
      x: (corner.lng - (origin?.lng ?? 0)) * metresPerLng,
      y: (corner.lat - (origin?.lat ?? 0)) * 110_540,
    }));
    const xs = points.map((point) => point.x);
    const ys = points.map((point) => point.y);
    return {
      string,
      points,
      minX: Math.min(...xs),
      maxX: Math.max(...xs),
      minY: Math.min(...ys),
      maxY: Math.max(...ys),
    };
  });

  const neighbours = strings.map(() => new Set<number>());
  for (let i = 0; i < projected.length; i++) {
    const a = projected[i];
    for (let j = i + 1; j < projected.length; j++) {
      const b = projected[j];
      if (
        a.string === b.string ||
        neighbours[a.string].has(b.string) ||
        b.minX - a.maxX > NEIGHBOUR_DISTANCE_M ||
        a.minX - b.maxX > NEIGHBOUR_DISTANCE_M ||
        b.minY - a.maxY > NEIGHBOUR_DISTANCE_M ||
        a.minY - b.maxY > NEIGHBOUR_DISTANCE_M
      )
        continue;
      const near = a.points.some((p) =>
        b.points.some(
          (q) => Math.hypot(p.x - q.x, p.y - q.y) <= NEIGHBOUR_DISTANCE_M
        )
      );
      if (near) {
        neighbours[a.string].add(b.string);
        neighbours[b.string].add(a.string);
      }
    }
  }

  const keys = new Map<number, number>();
  const neighbourKeys = (index: number) =>
    [...neighbours[index]].flatMap((other) => {
      const key = keys.get(other);
      return key == null ? [] : [key];
    });
  while (keys.size < strings.length) {
    let next = -1;
    let bestSaturation = -1;
    strings.forEach((_, index) => {
      if (keys.has(index)) return;
      const saturation = new Set(neighbourKeys(index)).size;
      if (
        saturation > bestSaturation ||
        (saturation === bestSaturation &&
          neighbours[index].size > neighbours[next].size)
      ) {
        next = index;
        bestSaturation = saturation;
      }
    });
    const taken = neighbourKeys(next);
    // With more neighbours than colours, this reuses the least-shared one.
    const uses = Array.from(
      { length: paletteSize },
      (_, candidate) => taken.filter((used) => used === candidate).length
    );
    keys.set(next, uses.indexOf(Math.min(...uses)));
  }
  return new Map(strings.map((string, index) => [string.id, keys.get(index)!]));
};
